<template>
  <div :class="progressClass" role="progressbar" :aria-valuenow="value" :aria-valuemin="min" :aria-valuemax="max">
    <div class="progress-track">
      <div 
        class="progress-fill"
        :style="progressStyle"
      >
        <div v-if="indeterminate" class="progress-indeterminate"></div>
      </div>
    </div>
    <div v-if="showLabel" class="progress-label">
      <slot>
        {{ labelText }}
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

export interface ProgressBarProps {
  value?: number;
  min?: number;
  max?: number;
  indeterminate?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'error';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  labelText?: string;
}

const props = withDefaults(defineProps<ProgressBarProps>(), {
  value: 0,
  min: 0,
  max: 100,
  indeterminate: false,
  variant: 'default',
  size: 'medium',
  showLabel: false,
});

const progressClass = computed(() => [
  'progress',
  `progress--${props.variant}`,
  `progress--${props.size}`,
  {
    'progress--indeterminate': props.indeterminate,
  }
]);

const progressPercentage = computed(() => {
  if (props.indeterminate) return 0;
  const range = props.max - props.min;
  const progress = Math.min(Math.max(props.value - props.min, 0), range);
  return (progress / range) * 100;
});

const progressStyle = computed(() => ({
  width: props.indeterminate ? '100%' : `${progressPercentage.value}%`,
}));

const labelText = computed(() => {
  if (props.labelText) return props.labelText;
  if (props.indeterminate) return 'Loading...';
  return `${Math.round(progressPercentage.value)}%`;
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.progress {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  
  &--small .progress-track {
    height: 4px;
  }
  
  &--medium .progress-track {
    height: 6px;
  }
  
  &--large .progress-track {
    height: 8px;
  }
  
  &--small .progress-label {
    font-size: 11px;
  }
  
  &--medium .progress-label {
    font-size: 12px;
  }
  
  &--large .progress-label {
    font-size: 13px;
  }
}

.progress-track {
  position: relative;
  background-color: var(--vscode-progressBar-background);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: var(--border-radius);
  transition: width 0.3s ease;
  position: relative;
  
  .progress--default & {
    background-color: var(--vscode-button-background);
  }
  
  .progress--success & {
    background-color: var(--vscode-testing-iconPassed);
  }
  
  .progress--warning & {
    background-color: var(--vscode-testing-iconSkipped);
  }
  
  .progress--error & {
    background-color: var(--vscode-testing-iconFailed);
  }
  
  .progress--indeterminate & {
    background: none;
  }
}

.progress-indeterminate {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 30%;
  border-radius: var(--border-radius);
  animation: progress-indeterminate 2s infinite ease-in-out;
  
  .progress--default & {
    background-color: var(--vscode-button-background);
  }
  
  .progress--success & {
    background-color: var(--vscode-testing-iconPassed);
  }
  
  .progress--warning & {
    background-color: var(--vscode-testing-iconSkipped);
  }
  
  .progress--error & {
    background-color: var(--vscode-testing-iconFailed);
  }
}

.progress-label {
  font-family: var(--vscode-font-family);
  color: var(--vscode-foreground);
  text-align: center;
  opacity: 0.8;
}

@keyframes progress-indeterminate {
  0% {
    left: -30%;
    transform: scaleX(0);
  }
  
  25% {
    left: -30%;
    transform: scaleX(1);
  }
  
  50% {
    left: 50%;
    transform: scaleX(1);
  }
  
  75% {
    left: 100%;
    transform: scaleX(1);
  }
  
  100% {
    left: 100%;
    transform: scaleX(0);
  }
}
</style>
